import { useEffect, useRef, useState } from "react";
import thumbNailImg from "../../assets/thumb01.jpg";

interface VideoSidebarProps {
    data: any[];
    currentIndex: number;
    onSelect: (index: number) => void;
    onClose: () => void;
    isAllDataFetched?: boolean;
    onLoadMore?: () => Promise<void>;
}

export default function VideoSidebar({
    data,
    currentIndex,
    onSelect,
    onClose,
    isAllDataFetched,
    onLoadMore,
}: VideoSidebarProps) {
    const listRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [loadingMore, setLoadingMore] = useState(false);
    // Prevent concurrent fetches
    const fetchingRef = useRef(false);

    /* ── Scroll active item into view when index changes ── */
    useEffect(() => {
        const el = listRef.current?.querySelector<HTMLElement>(
            `[data-sidebar-index="${currentIndex}"]`
        );
        el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, [currentIndex]);

    /* ── IntersectionObserver on sentinel ──
       Fires whenever the sentinel div at the bottom of the list
       enters the viewport — works even when the list is too short
       to scroll (the scroll-based approach fails in that case).
    ── */
    useEffect(() => {
        if (isAllDataFetched) return;

        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            async (entries) => {
                const entry = entries[0];
                if (entry.isIntersecting && !fetchingRef.current && !isAllDataFetched) {
                    fetchingRef.current = true;
                    setLoadingMore(true);
                    try {
                        await onLoadMore?.();
                    } finally {
                        setLoadingMore(false);
                        fetchingRef.current = false;
                    }
                }
            },
            {
                // Use the scrollable list as the root so the sentinel is
                // observed relative to the list container, not the viewport
                root: listRef.current,
                threshold: 0,
                rootMargin: "0px 0px 40px 0px",
            }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
        // Re-run when data length changes so the observer re-evaluates
        // after new items are appended
    }, [isAllDataFetched, onLoadMore, data.length]);

    return (
        <div className="flex flex-col h-full">
            {/* ── Header ── */}
            <div
                className="flex items-center justify-between px-4 py-3 shrink-0 border-b"
                style={{
                    borderColor: "rgba(255,255,255,0.08)",
                    background: "rgb(var(--surface-3))",
                }}
            >
                <span
                    className="text-[11px] font-semibold uppercase tracking-[0.1em]"
                    style={{ color: "rgb(var(--text-muted))" }}
                >
                    All Alerts
                </span>
                <span
                    className="text-[11px] px-2 py-0.5 rounded-full"
                    style={{
                        background: "rgba(245,158,11,0.15)",
                        color: "#f59e0b",
                        border: "1px solid rgba(245,158,11,0.2)",
                    }}
                >
                    {data.length}
                </span>
            </div>

            {/* ── Scrollable list ── */}
            <div ref={listRef} className="flex-1 overflow-y-auto">
                {data.map((item, i) => {
                    if (!item.video_link) return null;
                    const isActive = i === currentIndex;
                    return (
                        <button
                            key={item._id ?? i}
                            data-sidebar-index={i}
                            onClick={() => onSelect(i)}
                            className="w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors border-b"
                            style={{
                                background: isActive ? "rgba(245,158,11,0.08)" : "transparent",
                                borderColor: "rgba(255,255,255,0.06)",
                                borderLeft: isActive
                                    ? "3px solid #f59e0b"
                                    : "3px solid transparent",
                            }}
                        >
                            {/* Thumbnail */}
                            <div className="shrink-0 w-[60px] h-[38px] rounded overflow-hidden">
                                <img
                                    src={item.poster_url || thumbNailImg}
                                    alt=""
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = thumbNailImg;
                                    }}
                                />
                            </div>

                            {/* Text */}
                            <div className="flex-1 min-w-0">
                                <p
                                    className="text-[12px] font-medium truncate leading-tight"
                                    style={{
                                        color: isActive ? "#f59e0b" : "rgb(var(--text-primary))",
                                    }}
                                >
                                    {item.store_name || "—"}
                                </p>
                                <p
                                    className="text-[11px] truncate mt-0.5"
                                    style={{ color: "rgb(var(--text-muted))" }}
                                >
                                    {item.reason || "No reason"}
                                </p>
                            </div>

                            {/* Active dot */}
                            {isActive && (
                                <span
                                    className="shrink-0 w-1.5 h-1.5 rounded-full mt-1.5"
                                    style={{ background: "#f59e0b" }}
                                />
                            )}
                        </button>
                    );
                })}

                {/* ── Sentinel — observed by IntersectionObserver ── */}
                {!isAllDataFetched && (
                    <div ref={sentinelRef} style={{ height: "1px" }} />
                )}

                {/* ── Loading indicator ── */}
                {loadingMore && (
                    <div
                        className="flex items-center justify-center gap-2 py-3"
                        style={{ color: "rgb(var(--text-muted))" }}
                    >
                        <span
                            className="w-3 h-3 rounded-full border-2 border-t-transparent animate-spin"
                            style={{ borderColor: "#f59e0b", borderTopColor: "transparent" }}
                        />
                        <span className="text-[11px]">Loading more…</span>
                    </div>
                )}

                {/* ── End message ── */}
                {!loadingMore && isAllDataFetched && data.length > 0 && (
                    <div
                        className="text-center py-3 text-[11px]"
                        style={{ color: "rgb(var(--text-muted))" }}
                    >
                        All alerts loaded
                    </div>
                )}
            </div>
        </div>
    );
}
